import type { SeenMedplumClient } from '@seen/core/client'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import {
  queryOptions,
  skipToken,
  type UseQueryOptions,
  type UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import { NO_QUERY } from '#src/constants/no-query.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'

export function searchOptions<T extends ExtendedResource>(
  medplum: SeenMedplumClient,
  resourceClass: ExtendedResourceConstructor<T>,
  query: QueryTypes
): UseSuspenseQueryOptions<
  T[],
  Error,
  T[],
  ReturnType<typeof cacheKeys.searchExtended>
>

export function searchOptions<T extends ExtendedResource>(
  medplum: SeenMedplumClient,
  resourceClass: ExtendedResourceConstructor<T>,
  query: QueryTypes | undefined
): UseQueryOptions<T[], Error, T[], ReturnType<typeof cacheKeys.searchExtended>>

export function searchOptions<T extends ExtendedResource>(
  medplum: SeenMedplumClient,
  resourceClass: ExtendedResourceConstructor<T>,
  query: QueryTypes | undefined
):
  | UseSuspenseQueryOptions<
      T[],
      Error,
      T[],
      ReturnType<typeof cacheKeys.searchExtended>
    >
  | UseQueryOptions<
      T[],
      Error,
      T[],
      ReturnType<typeof cacheKeys.searchExtended>
    > {
  const parameters = normalizeQuery({ _count: 1000, ...query })
  const resourceType = resourceClass.RESOURCE_TYPE

  const searchKey = query
    ? getSearchKey(medplum, resourceType, parameters)
    : NO_QUERY

  return queryOptions({
    queryFn:
      searchKey === NO_QUERY
        ? skipToken
        : () => medplum.searchResourceClass(resourceClass, parameters),
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.searchExtended(resourceType, searchKey),
    meta: {
      queryParams: parameters,
      resourceClass,
    },
  })
}
